import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Rect, touchableClick } from '@sketch-hq/sketch-web-renderer'

import * as Styled from './EntityOverlay.styled'
import { SFSymbolLabel } from '@sketch/components'
import { ReactComponent as CheckIcon } from '@sketch/icons/check-16'
import { ReactComponent as CrossIcon } from '@sketch/icons/cross-16'

const VARIANT_PROPERTY_MIN_WIDTH = 30
const VARIANT_PROPERTY_GAP = 5

export type TouchableClickEvent<E extends HTMLElement> =
  | React.MouseEvent<E>
  | React.TouchEvent<E>

/**
 * The title button carries the UUID of the entity it belongs to, via the
 * same `data-artboard-uuid` attribute FrameOverlayContainer already sets on
 * the outer overlay - scoped to `button` so the lookup can't wander past the
 * header into the frame's own content, which sits under the same container.
 * The title is drawn in the gap above the entity's own bounds, which for a
 * nested entity can visually sit over an ancestor's area - consumers that
 * need to know exactly which entity's title was interacted with (e.g. a
 * right click, see WEB-3882) should read this instead of relying on
 * point-based hit testing.
 */
export function getHeaderEntityUUIDFromEventTarget(
  target: EventTarget | null
): string | null {
  if (!(target instanceof Element)) {
    return null
  }

  const button = target.closest('button[data-artboard-uuid]')

  // Below HEADER_MIN_WIDTH (see EntityOverlay.styled.ts) the title button is
  // collapsed to zero size rather than unmounted, so it stays in the DOM but
  // renders nothing a click could actually land on.
  if (!button || button.getClientRects().length === 0) {
    return null
  }

  return button.getAttribute('data-artboard-uuid')
}

export interface FrameOverlayEventHandlersProps {
  onOverlayClick?: (e: TouchableClickEvent<HTMLDivElement>) => void
  onOverlayDoubleClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onFrameHeaderMouseMove?: (e: React.MouseEvent<HTMLDivElement>) => void
  onFrameHeaderClick?: (e: TouchableClickEvent<HTMLDivElement>) => void
  onFrameClick?: (e: TouchableClickEvent<HTMLDivElement>) => void
  onCtaClick?: (e: TouchableClickEvent<HTMLButtonElement>) => void
}

export interface EntityOverlayProps extends FrameOverlayEventHandlersProps {
  bounds: Rect
  rootExtent?: Rect
  artboardUUID: string
  artboardName: string
  selected?: boolean
  /**
   * By default, hovering over the frame or its header will show a
   * hover state (orange rectangle around the frame content) added through CSS.
   * This behavior  can be disabled by this prop when we want to manage the hover state ourselves.
   */
  disableAutoHover?: boolean
  isComponent?: boolean
  /**
   * A Section grouping the variants of a component. It isn't a component
   * itself, but it takes the same accent colour so it reads as part of the
   * component family on the canvas.
   */
  isVariantSet?: boolean
  variantProperties?: readonly VariantProperty[]
  /**
   * Draws a dashed outline around the entity's bounds. Used for Sections with no
   * styling of their own - Sections no longer carry a default style, so an
   * unstyled one would otherwise leave no mark on the canvas. A styled Section
   * paints itself and doesn't need it.
   */
  showDashedOutline?: boolean
}

export interface VariantProperty {
  propertyName: string
  valueName: string
}

const formatArtboardBounds = (bounds: Rect, rootExtent?: Rect) => {
  /**
   * Given we are currently using 2 ways of positioning elements in the canvas
   * - Relative %
   * - Absolute
   *
   * It was decided that if "rootExtent" was passed, this would mean it's a relative to the parent calculation
   * if not it's the absolute position
   */
  if (!rootExtent) {
    return {
      x: `${bounds.x}px`,
      y: `${bounds.y}px`,
      width: `${bounds.width}px`,
      height: `${bounds.height}px`,
    }
  }

  const x = `${(bounds.x / rootExtent.width) * 100}%`
  const y = `${(bounds.y / rootExtent.height) * 100}%`
  const width = `${(bounds.width / rootExtent.width) * 100}%`
  const height = `${(bounds.height / rootExtent.height) * 100}%`

  return { x, y, width, height }
}

/**
 * Focuses the selected entity's CTA, so that Enter opens it.
 *
 * The alternative is a global keydown listener, which cannot tell what the user
 * is actually interacting with: it has to guess from `document.activeElement`,
 * and a toolbar button that merely kept focus from an earlier click looks
 * exactly like one the user is about to press. Giving the selected entity focus
 * puts the question to the browser instead - Enter reaches whatever is focused,
 * and the CTA already knows how to open its entity.
 *
 * `preventScroll` is what makes this safe. Focusing an element normally scrolls
 * it into view, which on this canvas translates the whole scene - the reason
 * the overlay has no tab index at all (see Cloud#18432). Focus without the
 * scroll and the camera does not move.
 *
 * Focus is not taken from anywhere the user is typing, and not from an open
 * menu, since Enter means something there already.
 *
 * Note that focus is what makes `swallowSpaceActivation` below necessary: the
 * browser activates a focused button on Space as well as Enter.
 */
function useFocusCtaWhenSelected(
  ctaRef: React.RefObject<HTMLButtonElement | null>,
  isSelectedAndOpenable: boolean
) {
  useEffect(() => {
    const cta = ctaRef.current

    if (!isSelectedAndOpenable) {
      // Hand focus back when this entity stops being the selected one. Holding
      // on to it would leave a focus ring on an entity that is no longer
      // selected, and would keep Enter pointed at it.
      if (cta && document.activeElement === cta) cta.blur()
      return
    }

    const active = document.activeElement
    const isBusy =
      active instanceof HTMLElement &&
      (active.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName) ||
        active.closest('[role="menuitem"]') !== null)

    if (isBusy) return

    cta?.focus({ preventScroll: true })
  }, [ctaRef, isSelectedAndOpenable])
}

/**
 * Stops Space from opening the selected entity.
 *
 * The CTA holds focus for as long as its entity is selected, and a focused
 * button is activated by Space as well as by Enter. On a canvas Space is not an
 * "activate" key - it is held to pan - so every attempt to pan with an entity
 * selected opened it instead. Now that a selection survives panning, switching
 * tabs and closing panels, that was easy to hit by accident.
 *
 * Preventing the default on keydown is what stops it: the click a button
 * synthesises for Space is dispatched on keyup, and only if the keydown was not
 * defaulted. Enter is untouched, and the event still bubbles, so anything
 * listening for Space to pan still hears it.
 */
const swallowSpaceActivation = (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key === ' ' || event.key === 'Spacebar') {
    event.preventDefault()
  }
}

/**
 * This element takes care of:
 * - Rendering the artboard name, cta and artboard outline.
 * - Tracking the user events on the artboard and emit them when needed.
 */
export const EntityOverlay = ({
  ref,
  ...props
}: EntityOverlayProps & {
  ref?: React.Ref<HTMLDivElement>
}) => {
  const {
    artboardUUID,
    artboardName,
    bounds,
    selected,
    disableAutoHover,
    rootExtent,
    onCtaClick,
    onOverlayClick,
    onOverlayDoubleClick,
    onFrameHeaderClick,
    onFrameHeaderMouseMove,
    onFrameClick,
    isComponent,
    isVariantSet,
    variantProperties = [],
    showDashedOutline,
  } = props

  const ctaRef = useRef<HTMLButtonElement>(null)
  useFocusCtaWhenSelected(ctaRef, Boolean(selected && onCtaClick))

  const { width, height, x, y } = useMemo(
    () => formatArtboardBounds(bounds, rootExtent),
    [bounds, rootExtent]
  )
  const overlayClickEvents = useTouchableClickHandler(onOverlayClick)

  const ctaClickEvents = useTouchableClickHandler(onCtaClick)
  const headerClickEvents = useTouchableClickHandler(onFrameHeaderClick)

  const frameClickEvents = useTouchableClickHandler(onFrameClick)
  const isVariant = variantProperties.length > 0

  return (
    <Styled.FrameOverlayContainer
      aria-selected={selected}
      data-testid="artboard-overlay"
      data-artboard-uuid={artboardUUID}
      ref={ref}
      /* Event handlers */
      onDoubleClick={onOverlayDoubleClick}
      {...overlayClickEvents}
      // This "tabIndex" is commented on purpose
      // to prevent the canvas from translating when a tab event happens
      // check https://github.com/sketch-hq/Cloud/issues/18432 to see the behaviour
      //
      // tabIndex={0}
      $disableHover={disableAutoHover}
      $isComponent={isComponent}
      $isVariantSet={isVariantSet}
      style={{
        left: x,
        top: y,
        width: width,
        height: height,
        cursor: onOverlayDoubleClick || onOverlayClick ? 'pointer' : 'inherit',
      }}
    >
      <Styled.ArtboardHeader
        {...headerClickEvents}
        onMouseMove={onFrameHeaderMouseMove}
        style={{
          cursor: onFrameHeaderClick ? 'pointer' : 'inherit',
        }}
      >
        <Styled.ArtboardCTA
          ref={ctaRef}
          // This "tabIndex" was added purposefully
          // to prevent the canvas from translating when a tab event happens
          // check https://github.com/sketch-hq/Cloud/issues/18432 to see the behaviour
          //
          tabIndex={-1}
          {...ctaClickEvents}
          onKeyDown={swallowSpaceActivation}
          data-artboard-uuid={artboardUUID}
          style={{
            cursor: onCtaClick ? 'pointer' : 'inherit',
          }}
        >
          <Styled.ArtboardName>
            {isVariant ? (
              selected ? (
                <VariantPropertySummary properties={variantProperties} />
              ) : (
                <VariantPropertyLabels properties={variantProperties} />
              )
            ) : (
              <SFSymbolLabel text={artboardName} />
            )}
          </Styled.ArtboardName>
          <Styled.ArrowIcon />
        </Styled.ArtboardCTA>
      </Styled.ArtboardHeader>
      {showDashedOutline && (
        <Styled.SectionOutline aria-hidden data-testid="section-outline">
          <rect width="100%" height="100%" />
        </Styled.SectionOutline>
      )}

      <Styled.FrameContentOverlay
        {...frameClickEvents}
        style={{
          cursor: onFrameClick ? 'pointer' : 'inherit',
        }}
      />
    </Styled.FrameOverlayContainer>
  )
}

function VariantPropertySummary({
  properties,
}: {
  properties: readonly VariantProperty[]
}) {
  return (
    <span
      aria-label={properties
        .map(({ propertyName, valueName }) => `${propertyName}: ${valueName}`)
        .join(', ')}
    >
      {properties.map((property, index) => (
        <React.Fragment
          key={`${property.propertyName}-${property.valueName}-${index}`}
        >
          {index > 0 && ' · '}
          <Styled.VariantPropertySummaryItem>
            <SFSymbolLabel text={getVariantPropertyLabel(property)} />
            <VariantBooleanIcon valueName={property.valueName} />
          </Styled.VariantPropertySummaryItem>
        </React.Fragment>
      ))}
    </span>
  )
}

function VariantPropertyLabels({
  properties,
}: {
  properties: readonly VariantProperty[]
}) {
  const containerRef = useRef<HTMLSpanElement>(null)
  const [labelsFit, setLabelsFit] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const requiredWidth =
      properties.length * VARIANT_PROPERTY_MIN_WIDTH +
      Math.max(0, properties.length - 1) * VARIANT_PROPERTY_GAP
    const updateLabelsFit = () =>
      setLabelsFit(container.clientWidth >= requiredWidth)

    updateLabelsFit()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(updateLabelsFit)
    observer.observe(container)

    return () => observer.disconnect()
  }, [properties.length])

  return (
    <Styled.VariantPropertyContainer
      ref={containerRef}
      $isVisible={labelsFit}
      aria-hidden={!labelsFit}
    >
      {properties.map(({ propertyName, valueName }, index) => {
        const isBoolean = isBooleanVariantValue(valueName)

        return (
          <Styled.VariantPropertyLabel
            key={`${propertyName}-${valueName}-${index}`}
            aria-label={`${propertyName}: ${valueName}`}
          >
            <Styled.VariantPropertyText>
              <SFSymbolLabel
                text={getVariantPropertyLabel({ propertyName, valueName })}
              />
            </Styled.VariantPropertyText>
            {isBoolean && <VariantBooleanIcon valueName={valueName} />}
          </Styled.VariantPropertyLabel>
        )
      })}
    </Styled.VariantPropertyContainer>
  )
}

function isBooleanVariantValue(valueName: string): boolean {
  return valueName === 'True' || valueName === 'False'
}

function getVariantPropertyLabel({ propertyName, valueName }: VariantProperty) {
  return isBooleanVariantValue(valueName) ? propertyName : valueName
}

function VariantBooleanIcon({ valueName }: Pick<VariantProperty, 'valueName'>) {
  if (!isBooleanVariantValue(valueName)) return null

  return (
    <Styled.VariantBooleanIcon aria-hidden>
      {valueName === 'True' ? <CheckIcon /> : <CrossIcon />}
    </Styled.VariantBooleanIcon>
  )
}

/**
 * Create a touch and a mouse click handler calling the same final handler function.
 * This is to handle both touch and mouse events in a consistent way.
 */
function useTouchableClickHandler<T extends HTMLElement>(
  handler?: (e: TouchableClickEvent<T>) => void
) {
  return useMemo(
    () => (handler ? touchableClick(handler) : undefined),
    [handler]
  )
}

EntityOverlay.displayName = 'EntityOverlay'

export default EntityOverlay
